import { useState } from 'react';
import { Table, Breadcrumb, Spin } from 'antd';
import { DownloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Row, Col } from 'components/Base/Grid';
import { useTheme } from 'styled-components';
import { useDispatch } from 'react-redux';
import {
  TInKindCategoryDB,
  TInKindCategoryTransactionDB,
} from 'data/types/inKind.types';
import { TInputOutputRow } from 'data/types/io.types';
import Button from 'components/Base/Button/Button';
import { Modal } from 'components/Base/Modal';
import { Collapse } from 'components/Base/Collapse';
import { EIconNames, Icon } from 'components/Base/Icon';
import {
  Title,
  Text,
  SecondaryText,
  Paragraph,
} from 'components/Base/Typography';
import ConfirmModalBody from 'components/Shared/ConfirmModalBody';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import {
  useDeleteInputOutputMutation,
  useLazyGetIoTransactionByIdQuery,
} from 'redux/apiSlice/io.slice';
import {
  useGetInKindQuarterDetailQuery,
  useLazyExportInKindTransactionsQuery,
  inKindApi,
} from 'redux/apiSlice/inKind.slice';
import EditInputOutputModal from 'components/Shared/EditInputOutputModal';
import { TQuarterDetailProps } from './InKindTab.types';
import { formatDollarValue } from './InKindTab.data';
import {
  SContentWrapper,
  SHeaderRow,
  SHeaderLeft,
  SBreadcrumbWrapper,
  SStatsRow,
  SStatBox,
  SCollapseLabelRow,
  SCollapseLabelLeft,
  SCategoryCollapse,
  SDeleteRecordInfo,
  SBreadcrumbLink,
} from './InKindTab.styles';

const QuarterDetail = ({
  elcId,
  selectedYear,
  selectedQuarter,
  onBack,
}: TQuarterDetailProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { showToastNotification } = useShowToastNotification();
  const [editRecord, setEditRecord] = useState<TInputOutputRow | null>(null);
  const [deleteRecord, setDeleteRecord] = useState<{
    resourceId: string;
    categoryName: string;
    dollarValue: number;
  } | null>(null);

  const { data, isLoading } = useGetInKindQuarterDetailQuery({
    elcId,
    year: selectedYear,
    quarter: selectedQuarter,
  });

  const [deleteInputOutput] = useDeleteInputOutputMutation();
  const [fetchTransaction] = useLazyGetIoTransactionByIdQuery();
  const [triggerExport, { isFetching: isExporting }] =
    useLazyExportInKindTransactionsQuery();

  const invalidateInKindCache = () => {
    dispatch(
      inKindApi.util.invalidateTags(['inKindSummary', 'inKindQuarterDetail']),
    );
  };

  const handleExportQuarter = () => {
    triggerExport({
      elcId,
      year: selectedYear,
      quarter: selectedQuarter,
    });
  };

  const handleEditSuccess = () => {
    setEditRecord(null);
    invalidateInKindCache();
    showToastNotification({
      message: 'In-kind line item updated successfully',
      type: ENotificationType.SUCCESS,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteRecord) return;
    try {
      await deleteInputOutput({
        resourceId: deleteRecord.resourceId,
      }).unwrap();
      setDeleteRecord(null);
      invalidateInKindCache();
      showToastNotification({
        message: 'In-kind line item deleted successfully',
        type: ENotificationType.SUCCESS,
      });
    } catch {
      showToastNotification({
        message: 'Failed to delete in-kind line item',
        type: ENotificationType.ERROR,
      });
    }
  };

  /** Line item columns inside expanded category */
  const lineItemColumns = [
    {
      title: 'Activity Date',
      dataIndex: 'date',
      key: 'date',
      width: 150,
      render: (val: string | null) => val ?? '-',
    },
    {
      title: 'Giving',
      dataIndex: 'giver',
      key: 'giver',
      width: 220,
      ellipsis: true,
      render: (val: string | null) => val ?? '-',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 130,
      render: (val: string | null) =>
        val ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase() : '-',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (val: number | string, record: TInKindCategoryTransactionDB) => {
        if (record.type === 'TIME' || record.type === 'Time') {
          const str = String(val);
          const match = str.match(/^(\d+):(\d+)$/);
          if (match) {
            const h = parseInt(match[1], 10);
            const m = parseInt(match[2], 10);
            return `${h}h ${m}m`;
          }
        }
        return typeof val === 'number' ? Math.round(val) : val;
      },
    },
    {
      title: '$$ Value',
      dataIndex: 'dollarValue',
      key: 'dollarValue',
      width: 140,
      align: 'right' as const,
      render: (val: number) => formatDollarValue(val),
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: 260,
      ellipsis: true,
      render: (val: string | null) => val ?? '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      align: 'center' as const,
      render: (_: unknown, record: TInKindCategoryTransactionDB) => (
        <Row justify="center" gutter={[12, 12]} wrap={false}>
          <Col
            onClick={async () => {
              try {
                const fullRecord = await fetchTransaction({
                  resourceId: record.resourceId,
                }).unwrap();
                setEditRecord(fullRecord);
              } catch {
                showToastNotification({
                  message: 'Failed to load transaction details',
                  type: ENotificationType.ERROR,
                });
              }
            }}
          >
            <Icon
              icon={EIconNames.EDIT_OUTLINED}
              color={theme.colorBlackIconColor}
              className="pointer"
              tooltip={{ title: 'Edit' }}
            />
          </Col>
          <Col
            onClick={() =>
              setDeleteRecord({
                resourceId: record.resourceId,
                categoryName: '',
                dollarValue: record.dollarValue,
              })
            }
          >
            <Icon
              icon={EIconNames.DELETE_OUTLINED}
              color={theme.colorBlackIconColor}
              className="pointer"
              tooltip={{ title: 'Delete' }}
            />
          </Col>
        </Row>
      ),
    },
  ];

  /** Build Collapse items from categories */
  const collapseItems = (data?.categories ?? []).map(
    (cat: TInKindCategoryDB) => ({
      key: cat.categoryId,
      label: (
        <SCollapseLabelRow>
          <SCollapseLabelLeft>
            <Text fontWeight={600}>{cat.categoryName}</Text>
            <SecondaryText>
              ({cat.transactions.length} item
              {cat.transactions.length !== 1 ? 's' : ''})
            </SecondaryText>
          </SCollapseLabelLeft>
          <Text fontWeight={600}>
            {formatDollarValue(cat.totalDollarValue)}
          </Text>
        </SCollapseLabelRow>
      ),
      children: (
        <Table
          dataSource={cat.transactions}
          columns={lineItemColumns}
          rowKey="resourceId"
          pagination={false}
          size="small"
          scroll={{ x: 1090 }}
        />
      ),
    }),
  );

  if (isLoading) {
    return (
      <SContentWrapper>
        <Spin style={{ display: 'block', margin: '80px auto' }} />
      </SContentWrapper>
    );
  }

  return (
    <SContentWrapper>
      {/* Breadcrumb */}
      <SBreadcrumbWrapper>
        <Breadcrumb
          items={[
            {
              title: (
                <SBreadcrumbLink
                  role="button"
                  tabIndex={0}
                  onClick={onBack}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onBack();
                    }
                  }}
                >
                  In-Kind Donations
                </SBreadcrumbLink>
              ),
            },
            { title: String(selectedYear) },
            { title: `Q${selectedQuarter}` },
          ]}
        />
      </SBreadcrumbWrapper>

      {/* Header: title + export */}
      <SHeaderRow>
        <SHeaderLeft>
          <Button
            variant="ghost"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            aria-label="Back to year view"
          />
          <Title level={4} className="mb-0">
            Q{selectedQuarter} {selectedYear} — In-Kind Donations
          </Title>
        </SHeaderLeft>
        <Button
          variant="default"
          icon={<DownloadOutlined />}
          onClick={handleExportQuarter}
          disabled={!data || data.totalTransactions === 0 || isExporting}
          loading={isExporting}
          aria-label="Export quarter to CSV"
        >
          Export Quarter CSV
        </Button>
      </SHeaderRow>

      {/* Stats row */}
      <SStatsRow>
        <SStatBox>
          <SecondaryText>Quarter Total</SecondaryText>
          <Title level={4} color={theme.colorPrimaryDefault} className="mb-0">
            {formatDollarValue(data?.totalDollarValue ?? 0)}
          </Title>
        </SStatBox>
        <SStatBox>
          <SecondaryText>Categories</SecondaryText>
          <Title level={4} className="mb-0">
            {data?.totalCategories ?? 0}
          </Title>
        </SStatBox>
        <SStatBox>
          <SecondaryText>Line Items</SecondaryText>
          <Title level={4} className="mb-0">
            {data?.totalTransactions ?? 0}
          </Title>
        </SStatBox>
      </SStatsRow>

      {/* Expandable categories */}
      {(data?.categories?.length ?? 0) > 0 ? (
        <SCategoryCollapse>
          <Collapse items={collapseItems} defaultActiveKey={[]} />
        </SCategoryCollapse>
      ) : (
        <SecondaryText className="text-center py-5 d-block">
          No in-kind donations recorded for Q{selectedQuarter} {selectedYear}.
        </SecondaryText>
      )}

      {/* Edit Modal: the shared I/O modal, so in-kind rows that are a Public
          Distribution or logged at an external event keep those details
          (GROW-4369) */}
      {editRecord && (
        <EditInputOutputModal
          mode="edit"
          open={!!editRecord}
          record={editRecord}
          sourceType="ELC"
          sourceId={elcId}
          isWorkshopOrEvent={false}
          onSuccess={handleEditSuccess}
          onCancel={() => setEditRecord(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteRecord}
        title="Delete In-Kind Line Item"
        cancelText="Cancel"
        okText="Yes, Delete"
        onCancel={() => setDeleteRecord(null)}
        onOk={handleDeleteConfirm}
        okButtonProps={{ danger: true }}
        width={400}
      >
        <ConfirmModalBody
          title="Are you sure?"
          description="This action will permanently delete this in-kind line item."
        />
        {deleteRecord && (
          <SDeleteRecordInfo>
            <div>
              <SecondaryText>$$ Value:</SecondaryText>{' '}
              <Text fontWeight={600}>
                {formatDollarValue(deleteRecord.dollarValue)}
              </Text>
            </div>
          </SDeleteRecordInfo>
        )}
        <Paragraph color="#ff4d4f" className="mt-3">
          This action cannot be undone. The underlying Outputs record will be
          deleted.
        </Paragraph>
      </Modal>
    </SContentWrapper>
  );
};

export default QuarterDetail;
